import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/files/")) return `${BACKEND_URL}${path}`;
  return path;
}

/**
 * Fullscreen cinematic lightbox.
 *
 * Props:
 *   items: array of { url, kind } (images and/or videos)
 *   index: current index (null = closed)
 *   onClose / onPrev / onNext: callbacks
 *
 * Behaviour:
 *   - Esc closes, ←/→ navigate
 *   - Click outside the media closes
 *   - Hides page scrollbar while open
 *   - Soft fade between slides, no jumping
 */
export default function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const open = index !== null && index !== undefined && items?.[index];

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    },
    [open, onClose, onPrev, onNext],
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("keydown", handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleKey]);

  // Touch swipe gestures — horizontal swipe navigates, vertical swipe
  // closes (Instagram-style).
  const touchStart = React.useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX > 60 && absX > absY) {
      if (dx < 0) onNext();
      else onPrev();
    } else if (absY > 80 && absY > absX) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[300] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.96)", backdropFilter: "blur(12px)" }}
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="lightbox-overlay"
        >
          {/* Close button — top right */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Chiudi"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-3 z-10"
            style={{ color: "rgba(255,255,255,0.85)" }}
            data-testid="lightbox-close"
          >
            <X size={22} strokeWidth={1.4} />
          </button>

          {/* Counter — top center */}
          <div
            className="absolute top-5 left-1/2 -translate-x-1/2 overline text-[10px] z-10"
            style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.3em" }}
            data-testid="lightbox-counter"
          >
            {index + 1} / {items.length}
          </div>

          {/* Desktop side arrows — hidden on mobile to avoid overlap */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              aria-label="Precedente"
              className="hidden sm:block absolute left-4 sm:left-8 p-3 z-10"
              style={{ color: "rgba(255,255,255,0.7)" }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={32} strokeWidth={1.2} />
            </button>
          )}

          {/* Media */}
          <motion.div
            key={items[index].url}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35 }}
            className="max-w-[96vw] max-h-[78vh] sm:max-h-[86vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {items[index].kind === "video" ? (
              <video
                src={buildUrl(items[index].url)}
                poster={items[index].poster_url ? buildUrl(items[index].poster_url) : undefined}
                controls
                playsInline
                preload="auto"
                // No autoplay / no muted: when the viewer presses play the
                // video starts with sound from the first millisecond. The
                // `poster` (when set) prevents the empty-frame flash; for
                // videos without a custom cover we still force a poster
                // frame via the onLoadedMetadata seek below.
                className="max-w-[96vw] max-h-[78vh] sm:max-h-[86vh] object-contain"
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                onLoadedMetadata={(e) => {
                  try {
                    if (e.currentTarget.currentTime < 0.05) {
                      e.currentTarget.currentTime = 0.1;
                    }
                  } catch {
                    /* best-effort */
                  }
                }}
              />
            ) : (
              <img
                src={buildUrl(items[index].url)}
                alt=""
                className="max-w-[96vw] max-h-[78vh] sm:max-h-[86vh] object-contain gd-noselect"
                draggable={false}
              />
            )}
          </motion.div>

          {/* Desktop right arrow */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              aria-label="Successiva"
              className="hidden sm:block absolute right-4 sm:right-8 p-3 z-10"
              style={{ color: "rgba(255,255,255,0.7)" }}
              data-testid="lightbox-next"
            >
              <ChevronRight size={32} strokeWidth={1.2} />
            </button>
          )}

          {/* Mobile bottom toolbar — prev/next anchored to the bottom edge so
              they never overlap the photo content */}
          {items.length > 1 && (
            <div
              className="sm:hidden absolute bottom-6 left-0 right-0 flex items-center justify-center gap-8 z-10"
              data-testid="lightbox-mobile-toolbar"
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                aria-label="Precedente"
                className="p-3 border"
                style={{
                  color: "rgba(255,255,255,0.85)",
                  borderColor: "rgba(255,255,255,0.2)",
                  background: "rgba(0,0,0,0.5)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="lightbox-prev-mobile"
              >
                <ChevronLeft size={20} strokeWidth={1.4} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                aria-label="Successiva"
                className="p-3 border"
                style={{
                  color: "rgba(255,255,255,0.85)",
                  borderColor: "rgba(255,255,255,0.2)",
                  background: "rgba(0,0,0,0.5)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="lightbox-next-mobile"
              >
                <ChevronRight size={20} strokeWidth={1.4} />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
